import React, { useState, useRef, useEffect } from 'react';
import { Mic, Image, Send, Plane, RotateCcw } from 'lucide-react';
import axios from 'axios';
import './App.css';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  type: 'text' | 'audio' | 'image';
  timestamp: Date;
  imageUrl?: string;
}

interface ChatResponse {
  response: string;
  status: string;
}

const API_BASE = ''; // Use relative URLs - no CORS needed when served from same origin
// const API_BASE = 'http://localhost:8080';

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [hasUploadedImage, setHasUploadedImage] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (text: string, sender: 'user' | 'bot', type: 'text' | 'audio' | 'image' = 'text', imageUrl?: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender,
      type,
      timestamp: new Date(),
      imageUrl
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const formatMessage = (text: string) => {
    // Simple formatting for all messages - no hardcoded detection
    return formatTextWithLinks(text);
  };

  const formatTextWithLinks = (text: string) => {
    // No processing needed - URL is already inline
    let processedText = text;
    
    // Split text into parts to handle URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = processedText.split(urlRegex);
    
    return (
      <div>
        {parts.map((part, index) => {
          if (part.match(urlRegex)) {
            return (
              <a 
                key={index} 
                href={part} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#075e54', textDecoration: 'underline' }}
              >
                {part}
              </a>
            );
          } else {
            // Handle **bold** text
            let formattedPart = part.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            
            // Handle ~strikethrough~ text
            formattedPart = formattedPart.replace(/~(.*?)~/g, '<del>$1</del>');
            
            // Handle bullet points: *   Text -> • Text
            formattedPart = formattedPart.replace(/^\*\s+(.+)$/gm, '• $1');
            
            // Split by line breaks and render as paragraphs
            const lines = formattedPart.split('\n').filter(line => line.trim());
            
            return (
              <React.Fragment key={index}>
                {lines.map((line, lineIndex) => {
                  // Check if this text part is followed by a URL - if so, render inline
                  const nextPart = parts[index + 1];
                  const isFollowedByUrl = nextPart && nextPart.match(urlRegex);
                  
                  if (isFollowedByUrl && lineIndex === lines.length - 1) {
                    // If this is the last line of this text part and followed by URL, render inline
                    return (
                      <span key={lineIndex} dangerouslySetInnerHTML={{ __html: line + ' ' }} />
                    );
                  } else {
                    return (
                      <p key={lineIndex} dangerouslySetInnerHTML={{ __html: line }} />
                    );
                  }
                })}
              </React.Fragment>
            );
          }
        })}
      </div>
    );
  };

  const sendTextMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText;
    setInputText('');
    
    // Add user message to chat
    addMessage(userMessage, 'user');
    
    // Reset image upload flag since we're sending a message
    setHasUploadedImage(false);
    
    setIsLoading(true);

    try {
      // Send text message (will automatically include any uploaded images)
      const response = await axios.post<ChatResponse>(`${API_BASE}/chat`, {
        message: userMessage,
        type: 'text'
      });
      
      addMessage(response.data.response, 'bot');
    } catch (error) {
      addMessage('Sorry, there was an error processing your request.', 'bot');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || isLoading) return;

    try {
      setIsLoading(true);
      
      // Create preview URL
      const imageUrl = URL.createObjectURL(file);
      
      // Upload image to backend first
      const formData = new FormData();
      formData.append('image', file);
      
      const uploadResponse = await axios.post(`${API_BASE}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Add image message to chat (just show the image)
      addMessage('', 'user', 'image', imageUrl);
      
      // Set flag to show image-specific placeholder
      setHasUploadedImage(true);
      
    } catch (error) {
      addMessage('Sorry, there was an error uploading your image.', 'bot');
    } finally {
      setIsLoading(false);
    }
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);

  const handleAudioRecord = async () => {
    if (isLoading) return;

    if (!isRecording) {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks: Blob[] = [];

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        recorder.onstop = async () => {
          const audioBlob = new Blob(chunks, { type: 'audio/wav' });
          
          // Send audio to backend
          setIsLoading(true);
          try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.wav');

            const response = await axios.post<ChatResponse>(`${API_BASE}/chat/audio`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            addMessage(response.data.response, 'bot');
            
            // Reset image upload flag since we sent audio
            setHasUploadedImage(false);
          } catch (error) {
            addMessage('Sorry, there was an error processing your audio.', 'bot');
          } finally {
            setIsLoading(false);
          }

          // Stop all tracks to release microphone
          stream.getTracks().forEach(track => track.stop());
        };

        setMediaRecorder(recorder);
        setAudioChunks(chunks);
        recorder.start();
        setIsRecording(true);
        
        // Show recording status
        addMessage('🎤 Recording... Press again to stop', 'user', 'audio');

      } catch (error) {
        addMessage('Sorry, could not access microphone. Please check permissions.', 'bot');
      }
    } else {
      // Stop recording
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
        setIsRecording(false);
        setMediaRecorder(null);
        
        // Update the last message to show recording stopped
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.text.includes('Recording...')) {
            lastMessage.text = '🎤 Audio recorded, processing...';
          }
          return newMessages;
        });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendTextMessage();
    }
  };

  const resetSession = async () => {
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      
      // Call backend to reset session
      await axios.post(`${API_BASE}/reset-session`);
      
      // Clear local state
      setMessages([]);
      setInputText('');
      setHasUploadedImage(false);
      
      // Stop any ongoing recording
      if (isRecording && mediaRecorder) {
        mediaRecorder.stop();
        setIsRecording(false);
      }
      
      // Clear audio chunks
      setAudioChunks([]);
      setMediaRecorder(null);
      
      console.log('Session reset successfully');
      
    } catch (error) {
      console.error('Error resetting session:', error);
      // Even if backend fails, clear local state
      setMessages([]);
      setInputText('');
      setHasUploadedImage(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      <div className="phone-container">
        <div className="phone-screen">
          {/* iPhone Status Bar */}
          <div className="status-bar">
            <div className="status-left">
              <span>10:00</span>
            </div>
            <div className="status-right">
              <div className="signal-bars">
                <div className="signal-bar"></div>
                <div className="signal-bar"></div>
                <div className="signal-bar"></div>
                <div className="signal-bar"></div>
              </div>
              <span className="wifi-icon">📶</span>
              <div className="battery-icon">
                <div className="battery-fill"></div>
              </div>
            </div>
          </div>
            <header className="app-header">
              <div className="header-content">
                <div className="company-logo">
                  <img src="/logo.jpeg" alt="IndiGo Logo" />
                </div>
                <div className="header-info">
                  <h1>
                    IndiGo
                    <div className="verified-badge">✓</div>
                  </h1>
                  <p className="header-subtitle">IndiGo 6E ✓</p>
                </div>
                <button
                  onClick={resetSession}
                  className="reset-button"
                  disabled={isLoading}
                  title="Reset conversation"
                >
                  <RotateCcw size={18} />
                </button>
              </div>
            </header>

            <div className="chat-container">
              <div className="messages">
                {messages.length === 0 && (
                  <div className="welcome-message">
                    <Plane size={48} />
                    <h2>Welcome to IndiGo Assistant!</h2>
                    <p>Ask me about flights, baggage policies, or check your flight status.</p>
                  </div>
                )}
                
                {messages.map((message) => (
                  <div key={message.id} className={`message ${message.sender}`}>
                    <div className="message-content">
                      {message.type === 'image' && message.imageUrl && (
                        <img src={message.imageUrl} alt="Uploaded" className="message-image" />
                      )}
                      {message.sender === 'bot' ? formatMessage(message.text) : <p>{message.text}</p>}
                      <span className="timestamp">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="message bot">
                    <div className="message-content loading">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              <div className="input-container">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                
                <div className="input-wrapper">
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={hasUploadedImage ? "Please speak or type your query about the image..." : "Type your message..."}
                    className="message-input"
                    rows={1}
                    disabled={isLoading}
                  />
                  
                  <div className="input-actions">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="action-button"
                      disabled={isLoading}
                      title="Upload image"
                    >
                      <Image size={20} />
                    </button>
                    
                    <button
                      onClick={handleAudioRecord}
                      className={`action-button ${isRecording ? 'recording' : ''}`}
                      disabled={isLoading}
                      title={isRecording ? "Stop recording" : "Start recording"}
                    >
                      <Mic size={20} />
                    </button>
                    
                    <button
                      onClick={sendTextMessage}
                      className="send-button"
                      disabled={isLoading || !inputText.trim()}
                      title="Send message"
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
      
      {/* Helper Info Panel */}
      <div className="info-panel">
        <div className="info-section">
          <h4>✈️ What I can help with:</h4>
          <ul className="help-list">
            <li>🔍 <strong>Flight Status:</strong> "Check flight 6E 123" or "Flight status for 6E123"</li>
            <li>📋 <strong>PNR Lookup:</strong> "Check flight status for PNR ABC123"</li>
            <li>👤 <strong>User Flights:</strong> "I'm Gupta" and "please tell me flight status"</li>
            <li>🎒 <strong>Baggage Policy:</strong> "What's the baggage allowance?"</li>
            <li>📷 <strong>Image Analysis:</strong> Upload boarding pass, ticket, or ID, then ask questions</li>
            <li>🎤 <strong>Voice Input:</strong> Click mic to speak your query</li>
            <li>🔄 <strong>Reset Session:</strong> Click the reset button (top-right) to clear conversation and start fresh</li>
            <li>🌍 <strong>Multilingual:</strong> Speak in Hindi, Marathi, Arabic, Spanish, or any language</li>
          </ul>
        </div>
        
        <div className="info-section">
          <h4>📋 Sample data to try:</h4>
          <div className="sample-grid">
            <div className="sample-item">
              <span className="sample-label">PNRs:</span>
              <span className="sample-value">ABC123, GHI789, PQR678, JKL012</span>
            </div>
            <div className="sample-item">
              <span className="sample-label">Flights:</span>
              <span className="sample-value">6E 123, 6E 456, 6E 789, 6E 234</span>
            </div>
            <div className="sample-item">
              <span className="sample-label">Names:</span>
              <span className="sample-value">Gupta, Reddy, Mittal, Johnson</span>
            </div>
          </div>
        </div>
        
        <div className="info-section">
          <h4>💡 Quick examples:</h4>
          <div className="example-list">
            <span className="example">"Check flight status for PNR ABC123"</span>
            <span className="example">"I'm Gupta, please tell me flight status"</span>
            <span className="example">"क्या मैं बंदूक ले जा सकता हूँ?" (Hindi - Can I carry a gun?)</span>
            <span className="example">"PNR साठी फ्लाइट स्टेटस काय आहे?" (Marathi - Flight status for PNR)</span>
            <span className="example">"શું હું ડબલ સીટ બુક કરી શકું?" (Gujarati - Can I book double seat?)</span>
            <span className="example">"ما هي سياسة الأمتعة؟" (Arabic - Baggage policy)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
